/**
 * Page shell for the Catalog section (/catalog): Products, Attributes and
 * Rules render as tabs. Import and Field Mappings are reached from buttons on
 * the Products tab rather than tabs of their own.
 */
import { Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { DashboardResources } from '@src/types';
import SectionTabShell, { type SectionTab } from '@src/components/SectionTabShell';

export type CatalogTab = 'products' | 'attributes' | 'rules';

export const CATALOG_TABS: SectionTab<CatalogTab>[] = [
  {
    value: 'products',
    label: 'Products',
    path: `/${DashboardResources.CatalogItems}`,
    resource: DashboardResources.CatalogItems,
  },
  {
    value: 'attributes',
    label: 'Attributes',
    path: `/${DashboardResources.CatalogCodeMappings}`,
    resource: DashboardResources.CatalogCodeMappings,
  },
  {
    value: 'rules',
    label: 'Rules',
    path: `/${DashboardResources.CatalogRules}`,
    resource: DashboardResources.CatalogRules,
  },
];

const TAB_TITLES: Record<CatalogTab, string> = {
  products: 'Catalog',
  attributes: 'Catalog Attributes',
  rules: 'Catalog Rules',
};

const TAB_DESCRIPTIONS: Record<CatalogTab, string> = {
  products: 'Browse and edit the products in your catalog.',
  attributes: 'Create and manage mappings between color/size codes and their corresponding display values across Trove\'s applications.',
  rules: 'Create if/then rules that modify catalog records for the selected partner.',
};

const ProductsActions = () => (
  <>
    <Button variant="outlined" component={Link} to={`/${DashboardResources.CatalogFieldsMappingUpload}`}>
      Field Mappings
    </Button>
    <Button variant="contained" component={Link} to={`/${DashboardResources.CatalogUploader}`}>
      Import
    </Button>
  </>
);

interface CatalogTabShellProps {
  tab: CatalogTab;
  children: React.ReactNode;
}

const CatalogTabShell = ({ tab, children }: CatalogTabShellProps) => (
  <SectionTabShell
    title={TAB_TITLES[tab]}
    description={TAB_DESCRIPTIONS[tab]}
    tabs={CATALOG_TABS}
    tab={tab}
    actions={tab === 'products' ? <ProductsActions /> : undefined}
  >
    {children}
  </SectionTabShell>
);

export default CatalogTabShell;
